import { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useUserContext } from "../hooks/UserContext";
import { useNotification } from "../hooks/NotificationContext";
import { fetchGetDetalleCuentaProveedor, fetchGetEstadosCuentaProveedores } from "../services/reporteService";

type Row = Record<string, unknown>;
const value = (row: Row, name: string) => row[name] ?? row[name.toLowerCase()] ?? "";
const money = (raw: unknown) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(Number(raw ?? 0));

/** Consulta saldos actuales de proveedores y sus movimientos registrados. */
export default function ReporteEstadosCuentasProveedores() {
  const { getUser } = useUserContext();
  const { showNotification } = useNotification();
  const [rows, setRows] = useState<Row[]>([]);
  const [detail, setDetail] = useState<Row[]>([]);
  const [selected, setSelected] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (!user) return;
    fetchGetEstadosCuentaProveedores(user)
      .then(setRows)
      .catch((error) => showNotification(error?.message ?? "No se pudo cargar el reporte.", "error"))
      .finally(() => setLoading(false));
  }, [getUser, showNotification]);

  const openDetail = (row: Row) => {
    const id = Number(value(row, "IDPROVEEDOR"));
    if (!id) return;
    setSelected(row);
    setLoadingDetail(true);
    const user = getUser();
    if (!user) return;
    fetchGetDetalleCuentaProveedor(user, id)
      .then(setDetail)
      .catch((error) => showNotification(error?.message ?? "No se pudo cargar el detalle.", "error"))
      .finally(() => setLoadingDetail(false));
  };

  const total = rows.reduce((sum, row) => sum + Number(value(row, "SALDO")), 0);
  return <Box sx={{ p: 2 }}>
    <Typography variant="h4" gutterBottom>Estados de cuenta de proveedores</Typography>
    <Alert severity="info" sx={{ mb: 2 }}>Saldos actuales de compras y pagos a cuenta corriente. Seleccioná un proveedor para revisar sus movimientos; este reporte no representa un corte histórico.</Alert>
    {loading ? <CircularProgress /> : rows.length === 0 ? <Alert severity="info">No hay movimientos de cuenta corriente para mostrar.</Alert> : <>
      <TableContainer component={Paper}><Table size="small"><TableHead><TableRow>{["Proveedor", "CUIT", "Teléfono", "Celular", "Comprado - pagado"].map((x) => <TableCell key={x}>{x}</TableCell>)}</TableRow></TableHead>
        <TableBody>{rows.map((row) => <TableRow key={String(value(row, "IDPROVEEDOR"))} hover onClick={() => openDetail(row)} sx={{ cursor: "pointer" }}>
          <TableCell>{String(value(row, "PROVEEDOR"))}</TableCell><TableCell>{String(value(row, "CUIT"))}</TableCell><TableCell>{String(value(row, "TELEFONO"))}</TableCell><TableCell>{String(value(row, "CELULAR"))}</TableCell><TableCell>{money(value(row, "SALDO"))}</TableCell>
        </TableRow>)}</TableBody></Table></TableContainer>
      <Typography sx={{ mt: 1, textAlign: "right" }} fontWeight="bold">Saldo total: {money(total)}</Typography>
    </>}
    {selected && <Box sx={{ mt: 4 }}><Typography variant="h5" gutterBottom>Movimientos: {String(value(selected, "PROVEEDOR"))}</Typography>
      {loadingDetail ? <CircularProgress /> : detail.length === 0 ? <Alert severity="info">No hay movimientos para este proveedor.</Alert> :
        <TableContainer component={Paper}><Table size="small"><TableHead><TableRow>{["Fecha", "Comprobante", "Importe"].map((x) => <TableCell key={x}>{x}</TableCell>)}</TableRow></TableHead><TableBody>{detail.map((row, index) => <TableRow key={index}><TableCell>{String(value(row, "FECHA"))}</TableCell><TableCell>{String(value(row, "FACTURA"))}</TableCell><TableCell>{money(value(row, "TOTAL"))}</TableCell></TableRow>)}</TableBody></Table></TableContainer>}
    </Box>}
  </Box>;
}
